import dayjs, { type Dayjs } from "dayjs";
import {
  type AnnualWheelEntry,
  type AnnualWheelRelease,
  type AnnualWheelWorkflowStep,
  type ReviewStatus,
} from "./types";

const DOCUMENT_TYPES = ["Policy", "Instruction", "Guideline", "Routine"];

const TOPICS = [
  "Information Security",
  "Anti-Money Laundering",
  "Risk Management",
  "Code of Conduct",
  "Remuneration",
  "Outsourcing",
  "Business Continuity",
  "Data Protection",
  "Whistleblowing",
  "Conflicts of Interest",
  "Credit",
  "Liquidity",
  "Capital Adequacy",
  "Internal Control",
  "Compliance",
  "Internal Audit",
  "Sustainability",
  "Complaints Handling",
  "Market Risk",
  "Operational Risk",
  "Insider Information",
  "Procurement",
  "Physical Security",
  "Incident Management",
  "Access Management",
  "Record Keeping",
  "Communication",
  "Tax",
  "Anti-Corruption",
  "Fit and Proper",
  "Product Governance",
  "Valuation",
  "Dividend",
  "Diversity",
];

// Shaped like the customer data: mostly shared windows, some shared starts
// with scattered ends, a few stragglers. The last one runs past New Year.
const REVIEW_WINDOWS = [
  { from: "01-13", to: "02-27", documents: 22 },
  { from: "03-02", to: "04-10", documents: 14 },
  { from: "05-04", to: "06-12", documents: 12 },
  { from: "05-04", to: "06-26", documents: 6 },
  { from: "07-06", to: "07-24", documents: 1 },
  { from: "08-17", to: "09-25", documents: 9 },
  { from: "09-14", to: "10-30", documents: 16 },
  { from: "09-14", to: "11-13", documents: 6 },
  { from: "11-02", to: "12-11", documents: 16 },
  { from: "11-16", to: "12-04", documents: 2 },
  { from: "12-14", to: "01-22", documents: 8 },
];

const WORKFLOW_STEPS = [
  { name: "Review", share: 0.35 },
  { name: "Legal review", share: 0.7 },
  { name: "Approval", share: 1 },
];

const OPEN_WINDOW_STATUSES: ReviewStatus[] = [
  "published",
  "inProgress",
  "notStarted",
];

const COMPLETED_STEPS: Record<ReviewStatus, number> = {
  notStarted: 0,
  inProgress: 1,
  published: WORKFLOW_STEPS.length,
};

const currentReviewStatus = (
  index: number,
  reviewFrom: Dayjs,
  reviewTo: Dayjs,
  today: Dayjs,
): ReviewStatus => {
  if (today.isBefore(reviewFrom)) return "notStarted";
  // A closed window keeps the odd document that never got published
  if (today.isAfter(reviewTo, "day")) {
    return index % 11 === 10 ? "inProgress" : "published";
  }

  return (
    OPEN_WINDOW_STATUSES[index % OPEN_WINDOW_STATUSES.length] ?? "notStarted"
  );
};

// Newer documents have a shorter history, so the earlier years thin out
const pastReleaseCount = (index: number) => {
  if (index % 7 === 6) return 0;
  if (index % 5 === 4) return 1;

  return 2 + (index % 4);
};

const stubWorkflowSteps = (
  index: number,
  reviewFrom: Dayjs,
  reviewTo: Dayjs,
  reviewStatus: ReviewStatus,
): AnnualWheelWorkflowStep[] => {
  const days = reviewTo.diff(reviewFrom, "day");

  return WORKFLOW_STEPS.map(({ name, share }, order) => {
    const isLastStep = order === WORKFLOW_STEPS.length - 1;
    const dueAt = reviewFrom
      .add(Math.round(days * share) - (isLastStep ? 0 : index % 3), "day")
      .toISOString();

    return {
      name,
      dueAt,
      completedAt: order < COMPLETED_STEPS[reviewStatus] ? dueAt : null,
    };
  });
};

interface StubReleaseInput {
  documentId: number;
  index: number;
  majorVersion: number;
  reviewStatus: ReviewStatus;
  reviewFrom: Dayjs;
  reviewTo: Dayjs;
}

const stubRelease = ({
  documentId,
  index,
  majorVersion,
  reviewStatus,
  reviewFrom,
  reviewTo,
}: StubReleaseInput): AnnualWheelRelease => ({
  id: documentId * 100 + majorVersion,
  majorVersion,
  reviewFrom: reviewFrom.toISOString(),
  reviewTo: reviewTo.toISOString(),
  status: reviewStatus,
  workflowSteps: stubWorkflowSteps(index, reviewFrom, reviewTo, reviewStatus),
});

// One history per document, as it stands today: a release for every year it has
// been reviewed, and the next review once the current release is published
const buildAnnualWheelEntries = (): AnnualWheelEntry[] => {
  const today = dayjs();
  const thisYear = today.year();
  const entries: AnnualWheelEntry[] = [];

  for (const { from, to, documents } of REVIEW_WINDOWS) {
    // An end earlier in the calendar than the start belongs to the following year
    const reviewPeriodIn = (year: number) => ({
      reviewFrom: dayjs(`${year}-${from}`),
      reviewTo: dayjs(`${to < from ? year + 1 : year}-${to}`),
    });
    // A review that runs past New Year stays the current one until it has closed
    const currentYear = today.isAfter(
      reviewPeriodIn(thisYear - 1).reviewTo,
      "day",
    )
      ? thisYear
      : thisYear - 1;
    const currentPeriod = reviewPeriodIn(currentYear);

    for (let i = 0; i < documents; i++) {
      const index = entries.length;
      const id = index + 1;
      const topicIndex = index % TOPICS.length;
      // Shifted per round of topics so every topic gets each document type once
      const typeIndex =
        (index + Math.floor(index / TOPICS.length)) % DOCUMENT_TYPES.length;

      const currentStatus = currentReviewStatus(
        index,
        currentPeriod.reviewFrom,
        currentPeriod.reviewTo,
        today,
      );
      const isCurrentPublished = currentStatus === "published";
      const firstYear = currentYear - pastReleaseCount(index);
      // Publishing a release schedules the review of the next one, a year later
      const lastYear = isCurrentPublished ? currentYear + 1 : currentYear;
      const reviewStatusIn = (year: number): ReviewStatus => {
        if (year < currentYear) return "published";

        return year === currentYear ? currentStatus : "notStarted";
      };

      const [latestRelease, ...previousReleases] = Array.from(
        { length: lastYear - firstYear + 1 },
        (_, age) => {
          const year = lastYear - age;

          return stubRelease({
            documentId: id,
            index,
            majorVersion: year - firstYear + 1,
            reviewStatus: reviewStatusIn(year),
            ...reviewPeriodIn(year),
          });
        },
      ) as [AnnualWheelRelease, ...AnnualWheelRelease[]];

      entries.push({
        id,
        name: `${TOPICS[topicIndex]} ${DOCUMENT_TYPES[typeIndex]}`,
        latestRelease,
        previousReleases,
      });
    }
  }

  return entries;
};

let entries: AnnualWheelEntry[] | undefined;

// Made once per page load, so every render sees the same documents
export const getAnnualWheelEntries = () =>
  (entries ??= buildAnnualWheelEntries());
